import { getSupabaseBrowserClient, resetSupabaseBrowserClient } from "./supabase-browser-client.js";
import { identifyPostHogUser } from "./posthog.js";

let ensureSessionPromise = null;
const SUPABASE_STORAGE_KEY = "mincool.supabase.auth";
const SUPABASE_QUERY_KEYS = ["code", "error", "error_code", "error_description", "sb"];
const SUPABASE_HASH_KEYS = [
  "access_token",
  "refresh_token",
  "expires_at",
  "expires_in",
  "token_type",
  "type",
  "provider_token",
  "provider_refresh_token",
  "error",
  "error_code",
  "error_description",
  "sb",
];
const SUPABASE_SAME_BROWSER_ERROR_CODES = ["flow_state_not_found", "pkce_code_verifier_not_found"];

function getStorageTargets() {
  if (typeof window === "undefined") {
    return [];
  }
  return [window.localStorage, window.sessionStorage].filter(Boolean);
}

function isSupabaseStorageKey(key = "") {
  const normalizedKey = String(key || "").trim();
  if (!normalizedKey) {
    return false;
  }
  return normalizedKey === SUPABASE_STORAGE_KEY
    || normalizedKey.startsWith("sb-")
    || normalizedKey.includes("supabase.auth")
    || normalizedKey.includes("code-verifier");
}

function getClientOrThrow() {
  const client = getSupabaseBrowserClient();
  if (!client) {
    throw new Error("Supabase is not configured.");
  }
  return client;
}

function syncPostHogFromSession(session = null) {
  const user = session?.user || null;
  if (!user || user.is_anonymous) {
    return;
  }
  identifyPostHogUser(user);
}

export async function ensureAnonymousSession() {
  if (!ensureSessionPromise) {
    ensureSessionPromise = (async () => {
      const client = getClientOrThrow();
      const {
        data: { session },
        error,
      } = await client.auth.getSession();
      if (error) {
        throw error;
      }
      if (session) {
        syncPostHogFromSession(session);
        return session;
      }
      const { data, error: signInError } = await client.auth.signInAnonymously();
      if (signInError) {
        throw signInError;
      }
      if (!data.session) {
        throw new Error("Could not start an anonymous session.");
      }
      syncPostHogFromSession(data.session);
      return data.session;
    })().finally(() => {
      ensureSessionPromise = null;
    });
  }
  return ensureSessionPromise;
}

async function getSession({ allowAnonymousBootstrap = false } = {}) {
  const client = getClientOrThrow();
  const {
    data: { session },
    error,
  } = await client.auth.getSession();
  if (error) {
    throw error;
  }
  if (session) {
    syncPostHogFromSession(session);
    return session;
  }
  if (!allowAnonymousBootstrap) {
    return null;
  }
  return ensureAnonymousSession();
}

export async function getAccessToken({ allowAnonymousBootstrap = false } = {}) {
  const session = await getSession({ allowAnonymousBootstrap });
  if (!session?.access_token) {
    throw new Error("Authentication required");
  }
  return session.access_token;
}

function resolveUrl(href) {
  if (!href) {
    return null;
  }
  const base =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost";
  return new URL(href, base);
}

function readHashParams(url) {
  const hash = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
  return hash ? new URLSearchParams(hash) : null;
}

function formatSupabaseAuthErrorMessage({ errorCode = "", errorDescription = "" } = {}) {
  const normalizedCode = String(errorCode || "").trim().toLowerCase();
  const normalizedDescription = String(errorDescription || "").trim();

  if (
    normalizedCode === "otp_expired"
    || /invalid or has expired|expired/i.test(normalizedDescription)
  ) {
    return "This email link is invalid or expired. Request a new email or enter the 6-digit code from the latest message.";
  }

  if (SUPABASE_SAME_BROWSER_ERROR_CODES.includes(normalizedCode)) {
    return "Open the newest email link in the same browser where you requested it, or enter the 6-digit code instead.";
  }

  if (normalizedDescription) {
    return normalizedDescription;
  }

  return "We couldn't complete that email link. Request a new email or enter the 6-digit code instead.";
}

export function readSupabaseAuthCallbackFromUrl(href) {
  const url = resolveUrl(href);
  if (!url) {
    return null;
  }

  const code = url.searchParams.get("code");
  if (code) {
    return {
      type: "pkce",
      code,
    };
  }

  const params = readHashParams(url);
  if (!params) {
    return null;
  }
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");

  if (!accessToken || !refreshToken) {
    return null;
  }

  return {
    type: "implicit",
    accessToken,
    refreshToken,
  };
}

export function readSupabaseAuthErrorFromUrl(href) {
  const url = resolveUrl(href);
  if (!url) {
    return null;
  }

  const hashParams = readHashParams(url);
  const sources = [hashParams, url.searchParams].filter(Boolean);

  for (const params of sources) {
    if (!params.has("error") && !params.has("error_code") && !params.has("error_description")) {
      continue;
    }

    const error = String(params.get("error") || "").trim();
    const errorCode = String(params.get("error_code") || "").trim();
    const errorDescription = String(params.get("error_description") || "").trim();

    return {
      error,
      errorCode,
      errorDescription,
      message: formatSupabaseAuthErrorMessage({ errorCode, errorDescription }),
    };
  }

  return null;
}

export function stripSupabaseAuthCallbackFromUrl(href) {
  const url = resolveUrl(href);
  if (!url) {
    return "";
  }

  for (const key of SUPABASE_QUERY_KEYS) {
    url.searchParams.delete(key);
  }

  const params = readHashParams(url);
  if (params) {
    const hasSupabaseAuthParams = SUPABASE_HASH_KEYS.some((key) => params.has(key));
    if (hasSupabaseAuthParams) {
      url.hash = "";
    }
  }

  return url.toString();
}

export async function syncSupabaseSessionFromUrl({ client = null, location = null, history = null } = {}) {
  const resolvedClient = client || getSupabaseBrowserClient();
  if (!resolvedClient) {
    return false;
  }

  const resolvedLocation = location || (typeof window !== "undefined" ? window.location : null);
  if (!resolvedLocation?.href) {
    return false;
  }

  const callback = readSupabaseAuthCallbackFromUrl(resolvedLocation.href);
  if (!callback) {
    return false;
  }

  ensureSessionPromise = null;

  const result =
    callback.type === "pkce"
      ? await resolvedClient.auth.exchangeCodeForSession(callback.code)
      : await resolvedClient.auth.setSession({
          access_token: callback.accessToken,
          refresh_token: callback.refreshToken,
        });

  if (result.error) {
    throw result.error;
  }
  syncPostHogFromSession(result.data?.session || null);

  const nextUrl = stripSupabaseAuthCallbackFromUrl(resolvedLocation.href);
  const resolvedHistory = history || (typeof window !== "undefined" ? window.history : null);
  if (resolvedHistory?.replaceState) {
    resolvedHistory.replaceState(resolvedHistory.state, "", nextUrl);
  }

  ensureSessionPromise = null;
  return true;
}

export function clearSupabaseSessionStorage() {
  for (const store of getStorageTargets()) {
    const keysToClear = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (isSupabaseStorageKey(key)) {
        keysToClear.push(key);
      }
    }
    for (const key of keysToClear) {
      store.removeItem(key);
    }
  }
}

export async function resetBrowserSupabaseSession() {
  const client = getSupabaseBrowserClient();
  ensureSessionPromise = null;
  clearSupabaseSessionStorage();
  if (!client) {
    resetSupabaseBrowserClient();
    return;
  }
  try {
    // Local QA should clear only the current browser session before re-entering anonymously.
    await client.auth.signOut({ scope: "local" });
  } catch {
    // Ignore transient auth errors and still hard-reset the browser client below.
  } finally {
    clearSupabaseSessionStorage();
    resetSupabaseBrowserClient();
    ensureSessionPromise = null;
  }
}
